import * as A from 'fp-ts-esm/Array';
import { pipe } from 'fp-ts-esm/function';
import { fromPredicate } from 'fp-ts-esm/Option';
import * as T from 'fp-ts-esm/Tree';
import type { Traversal } from 'monocle-ts-esm';
import { Lens, Prism, fromTraversable } from 'monocle-ts-esm';

/**
 *
 *
 * Tree-like structure feature extractor
 * @template DT extraction unit data type
 */
export interface Extractor<DT> {
  filter: (predicate: (data: DT) => boolean) => Extractor<DT>;
  map: <MT>(mapper: (data: DT) => MT) => Extractor<MT>;
  chain: <CT>(mapper: (data: DT) => Extractor<CT>) => Extractor<CT>[];
  get: () => DT[];
}

/**
 *
 *
 * @template NodeT tree node type
 * @param {(node: NodeT) => NodeT[]} nodesAccessor how to access child nodes
 * @param {(root: NodeT)} root tree root (curried)
 */
export const getExtractor =
  <NodeT>(nodesAccessor: (node: NodeT) => NodeT[]) =>
  (root: NodeT): Extractor<NodeT> => {
    class TraversalExtractor<DT> implements Extractor<DT> {
      traversal: Traversal<T.Tree<NodeT>, DT>;

      tree: T.Tree<NodeT>;

      constructor(traversal: Traversal<T.Tree<NodeT>, DT>, tree: T.Tree<NodeT>) {
        this.traversal = traversal;
        this.tree = tree;
      }

      // @ts-ignore $TSFixMe
      filter(predicate: (data: DT) => boolean) {
        return new TraversalExtractor<DT>(
          this.traversal.composePrism(Prism.fromPredicate(predicate)),
          this.tree
        );
      }

      // @ts-ignore $TSFixMe
      map<MT>(mapper: (data: DT) => MT) {
        return new TraversalExtractor<MT>(
          this.traversal.composeLens(new Lens<DT, MT>(mapper, () => (dt: DT) => dt)),
          this.tree
        );
      }

      // @ts-ignore $TSFixMe
      chain<CT>(mapper: (data: DT) => TraversalExtractor<CT>): Extractor<CT>[] {
        // @ts-ignore $TSFixMe
        return pipe(this.get(), A.map(mapper));
      }

      get() {
        return pipe(
          this.traversal.asFold().getAll(this.tree),
          A.map(fromPredicate(Boolean)),
          A.compact
        );
      }
    }

    const tree = T.unfoldTree<NodeT, NodeT>(root, node => [node, nodesAccessor(node)]);
    // @ts-ignore $TSFixMe
    return new TraversalExtractor(fromTraversable(T.tree)<NodeT>(), tree);
  };
